import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { faFileExport, faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { IParamsValue } from 'src/app/models/interfaces/params-value.interface';
import { IParamsValues } from 'src/app/models/interfaces/params-values.interface';
import { ParametersValueFormComponent } from '../parameters-value-form/parameters-value-form.component';
import { ParametersValuesFormContext } from './parameters-values-form-context';

@Component({
    selector: 'app-parameters-values-form',
    templateUrl: './parameters-values-form.component.html',
    styleUrls: ['./parameters-values-form.component.scss'],
    imports: [ParametersValueFormComponent, MatButtonModule, NgClass]
})
export class ParametersValuesFormComponent implements OnInit {
    public faFileExport = faFileExport;

    public faSave = faSave;

    @ViewChildren('pvfc') components: QueryList<ParametersValueFormComponent>;

    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Infos du client à afficher dans le formulaire.
     */
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('paramsValues')
    public _dataInput: Required<IParamsValues> | null | undefined;

    public get dataInput(): Required<IParamsValues> | null | undefined {
        return this._dataInput;
    }

    public set dataInput(value: Required<IParamsValues> | null | undefined) {
        value.paramsSetsParams = value.paramsSetsParams.sort((a, b) => {
            return a.order < b.order ? -1 : 1;
        });
        this._dataInput = value;
    }

    /**
     * Valeur des paramètres pour le groupe parent (utilisé pour l'héritage).
     */
    @Input()
    public hierarchyValues: IParamsValues;

    /**
     * Valeur indiquant si le formulaire est disabled.
     */
    @Input()
    public disabled: boolean;

    /**
     * Indioque si le fomulaire est valide.
     */
    public formIsValid: boolean = true;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: ParametersValuesFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<ParametersValuesFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: ParametersValuesFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = true;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('paramsValuesUpdated')
    public dataOutput: EventEmitter<IParamsValues> = new EventEmitter();

    @Output()
    public paramsExported: EventEmitter<Boolean> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    constructor(public enumHelper: EnumHelper) {}
    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
        });
    }

    /**
     * Mise à jour des paramètres
     * @param paramsValue : Inforamtion du paramètre à mettre à jour.
     */
    public paramsValueUpdated(paramsValue: IParamsValue): void {
        let paramsSetsParams = this.dataInput.paramsSetsParams.find((x) => x.paramName === paramsValue.paramName);
        paramsSetsParams.value = paramsValue.value;

        if (paramsSetsParams.isInherited === true && paramsValue.isInherited === false) {
            // Si le paramètre était hérité, un nouveau paramètre non hérité doit être créé (L'id n'est donc pas défini)
            paramsSetsParams.isInherited = false;
            paramsSetsParams.id = null;
        } else if (paramsSetsParams.isInherited === false && paramsValue.isInherited === true) {
            paramsSetsParams.isInherited = true;
        }

        this.formIsValid = true;
        this.components.forEach((component) => {
            if (component.formGroup.invalid) {
                this.formIsValid = false;
            }
        });
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public manualSave(): void {
        if (this.formIsValid) {
            this.dataOutput.emit(this.dataInput);
        }
    }

    /**
     * Recupère la valeur du parent pour un paramètre
     * @param paramName : nom du paramètre.
     * @returns : Indique la valeur du parent.
     */
    public parentValue(paramName: string): string {
        if (!!this.hierarchyValues) {
            return this.hierarchyValues.paramsSetsParams.find((x) => x.paramName == paramName).value;
        } else {
            return null;
        }
    }

    onExported() {
        this.paramsExported.emit(true);
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
